import type { KeyboardEvent } from 'react';
import { useMemo } from 'react';

import type { IDayObject } from './types';

import { Keyboard } from '@snowit/iceconst';
import testId from '@snowit/utils/testId';

import Status from '../../Status';

import cx from 'classnames';
import s from './CalendarDay.module.scss';

interface ICalendarDay {
  day: IDayObject;
  onClick: (date: IDayObject) => void;
  ranges: string[][];
  availability?: string;
  disabled?: boolean;
  stockDisabled?: boolean;
}

const CalendarDay = (props: ICalendarDay) => {
  const { day, onClick, ranges, availability, stockDisabled = false, disabled = false } = props;
  const { value, in_view, number } = day;
  const index = ranges.findIndex((arr) => arr.includes(value));
  const range = ranges[index] || [];

  const handleClick = () => {
    onClick(day);
  };

  const handleKeydown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== Keyboard.ENTER) return;
    handleClick();
  };

  const selection = useMemo(() => {
    if (!range.length || disabled) return '';
    if (range.length === 1) return 'selected';
    if (range[0] === value) return 'start';
    if (range[range.length - 1] === value) return 'end';
    return 'middle';
  }, [range, index, value]);

  const classes = cx({
    [s.day]: true,
    [s.today]: day.today,
    [s.selected]: selection === 'selected',
    [s.selectedStart]: selection === 'start',
    [s.selectedMiddle]: selection === 'middle',
    [s.selectedEnd]: selection === 'end',
    [s.breakLeft]: day.break_left,
    [s.breakRight]: day.break_right,
    [s.hidden]: !in_view,
    [s.disabled]: disabled,
  });

  const renderAvailability = () => {
    if (!availability) return null;
    if (stockDisabled) return null;
    return (
      <Status
        classNames={{
          wrapper: s.wrapperAvailability,
        }}
        status={availability}
        test={`date_${value}_${availability}`}
        theme={'icon'}
      />
    );
  };

  return (
    <div
      className={classes}
      aria-disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeydown}
      {...testId(`date_${value}`)}
      data-date={value}
      role={'button'}
      tabIndex={disabled || !in_view ? -1 : 0}
    >
      <div className={s.date}>{number}</div>
      {renderAvailability()}
    </div>
  );
};

export default CalendarDay;
